// src/pages/MIS.js
import React, { useEffect, useMemo, useState } from "react";
import {
  FiBookOpen,
  FiMessageCircle,
  FiSend,
  FiEdit2,
  FiSave,
  FiX,
  FiUpload,
  FiStar,
  FiHeart,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../utils/axiosInstance";
import "../styles/MIS.css";

const FALLBACK_MODELS = [
  { code: "GENEL", name: "Genel" },
  { code: "C1XD", name: "C1XD" },
  { code: "SEVEN_XD", name: "Seven XD" },
  { code: "C1", name: "C1" },
  { code: "V3", name: "V3" },
  { code: "M4", name: "M4" },
];

// MIS için varsayılan iletişim bilgileri
// MIS için varsayılan iletişim bilgileri
const MIS_DEFAULT_CONTACT = {
  phone: "0 312 260 53 53 – 0 553 379 27 53",
  email: "misdentpoliklinigi@gmail.com",
  website: "https://misdent.com.tr",
  note:
    "Cevizlidere Mah. Cevizlidere Cad. No:27/A\n" +
    "Çankaya / Ankara",
};

export default function MIS() {
  const { t } = useTranslation();
  const [uiLang, setUiLang] = useState(localStorage.getItem("ui_lang") || "tr");
  // Auth
  const [me, setMe] = useState(null); // { id, username, role, ... }
  const [authChecked, setAuthChecked] = useState(false);

  // Brand (MIS) iletişim
  const [brand, setBrand] = useState(null);
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({
    phone: "",
    email: "",
    website: "",
    note: "",
  });

  // Yeni thread formu
  const [models, setModels] = useState(FALLBACK_MODELS);
  const [modelCode, setModelCode] = useState("C1XD");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [posting, setPosting] = useState(false);

  // Liste + filtreler
  const [threads, setThreads] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("latest"); // latest | top
  const [filterModel, setFilterModel] = useState("ALL");

  // Cevap formu
  const [replyText, setReplyText] = useState({});
  const [replyImage, setReplyImage] = useState({});
  const [replyPosting, setReplyPosting] = useState({});

  const mustLogin = useMemo(() => !me && authChecked, [me, authChecked]);

  // 🔑 Önemli: Giriş yapmış HERKES için post yetkisi
  const canPost = !!me;


  /* ==================== Profil ==================== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get("/api/auth/me/");
        if (mounted) setMe(data);
      } catch {
        // not logged in
      } finally {
        if (mounted) setAuthChecked(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onLang = () => {
      setUiLang(localStorage.getItem("ui_lang") || "tr");
    };
    window.addEventListener("lang:change", onLang);
    return () => window.removeEventListener("lang:change", onLang);
  }, []);

  /* ==================== MIS Brand ==================== */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get("/api/social/brands/mis/");
        if (!mounted) return;

        setBrand(data);

        const contact = data?.contact;
        setContactDraft({
          phone: contact?.phone || MIS_DEFAULT_CONTACT.phone,
          email: contact?.email || MIS_DEFAULT_CONTACT.email,
          website: contact?.website || MIS_DEFAULT_CONTACT.website,
          note: contact?.note || MIS_DEFAULT_CONTACT.note,
        });

        if (Array.isArray(data?.models) && data.models.length) {
          const list = [...data.models].sort((a, b) =>
            a.code === "C1XD"
              ? -1
              : b.code === "C1XD"
              ? 1
              : a.name.localeCompare(b.name)
          );
          setModels(list);
        }
      } catch {
        // fallback modeller kalır
        setContactDraft(MIS_DEFAULT_CONTACT);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [uiLang]); // ✅ uiLang değişince brand'i yeniden çek

  /* ==================== Threads ==================== */
  const fetchThreads = async () => {
    setLoadingList(true);
    try {
      const params = { brand: "mis", sort };
      if (filterModel !== "ALL") params.model = filterModel;
      if (onlyMine) params.mine = "1";
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get("/api/social/threads/", { params });
      setThreads(Array.isArray(data?.results) ? data.results : data);
    } catch {
      setThreads([]);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (authChecked) fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, onlyMine, q, sort, filterModel, uiLang]);


  /* ==================== Like: Cevap ==================== */
  const toggleLike = async (postId) => {
    try {
      const { data } = await axios.post("/api/social/likes/toggle/", {
        post_id: postId,
      });
      setThreads((prev) =>
        prev.map((th) => ({
          ...th,
          posts: th.posts?.map((p) =>
            p.id === postId
              ? { ...p, liked: data.liked, likes_count: data.likes_count }
              : p
          ),
        }))
      );
    } catch (e) {
      if (e?.response?.status === 401) alert(t("misPage.loginToLike"));
    }
  };

  /* ==================== Like: Soru ==================== */
  const toggleThreadLike = async (threadId) => {
    try {
      const { data } = await axios.post("/api/social/thread-likes/toggle/", {
        thread_id: threadId,
      });
      setThreads((prev) =>
        prev.map((th) =>
          th.id === threadId
            ? { ...th, liked: data.liked, likes_count: data.likes_count }
            : th
        )
      );
    } catch (e) {
      if (e?.response?.status === 401) alert(t("misPage.loginToLike"));
    }
  };

  /* ==================== Yeni Thread ==================== */
  const submitThread = async (e) => {
    e.preventDefault();
    if (!me) {
      alert(t("misPage.loginToPost"));
      return;
    }

    if (!subject.trim()) {
      alert(t("misPage.subjectRequired"));
      return;
    }
    if (subject.trim().length > 10) {
      alert(t("misPage.subjectTooLong"));
      return;
    }
    if (!modelCode) {
      alert(t("misPage.selectModel"));
      return;
    }

    setPosting(true);
    try {
      const { data: csrf } = await axios.get("/api/auth/csrf/");
      const fd = new FormData();
      fd.append("brand", "mis");
      fd.append("model", modelCode);
      fd.append("subject", subject.trim());
      fd.append("body", body.trim());
      if (imageFile) fd.append("image", imageFile);

      await axios.post("/api/social/threads/", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRFToken": csrf?.csrfToken,
        },
        withCredentials: true,
      });

      setSubject("");
      setBody("");
      setImageFile(null);
      await fetchThreads();
    } catch (err) {
      const data = err?.response?.data;
      if (data) {
        if (typeof data.detail === "string") {
          alert(data.detail);
        } else if (data.subject && Array.isArray(data.subject)) {
          alert(t("misPage.subjectErrorPrefix") + data.subject.join(" "));
        } else {
          alert(t("misPage.formErrorPrefix") + JSON.stringify(data));
        }
      } else {
        alert(t("misPage.submitError"));
      }
    } finally {
      setPosting(false);
    }
  };

  /* ==================== Cevap ekle ==================== */
  const submitReply = async (threadId) => {
    if (!me) {
      alert(t("misPage.loginToReply"));
      return;
    }
    const txt = (replyText[threadId] || "").trim();
    if (!txt) {
      alert(t("misPage.replyEmpty"));
      return;
    }

    setReplyPosting((s) => ({ ...s, [threadId]: true }));
    try {
      const { data: csrf } = await axios.get("/api/auth/csrf/");
      const fd = new FormData();
      fd.append("thread", threadId);
      fd.append("body", txt);
      if (replyImage[threadId]) fd.append("image", replyImage[threadId]);

      await axios.post("/api/social/posts/", fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-CSRFToken": csrf?.csrfToken,
        },
        withCredentials: true,
      });

      setReplyText((s) => ({ ...s, [threadId]: "" }));
      setReplyImage((s) => ({ ...s, [threadId]: null }));
      await fetchThreads();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.detail) alert(data.detail);
      else alert(t("misPage.replyFailed"));
    } finally {
      setReplyPosting((s) => ({ ...s, [threadId]: false }));
    }
  };

  /* ==================== Brand contact kaydet ==================== */
  const saveContact = async () => {
    try {
      const { data: csrf } = await axios.get("/api/auth/csrf/");
      await axios.patch(
        "/api/social/brands/mis/",
        { contact: contactDraft },
        {
          headers: { "X-CSRFToken": csrf?.csrfToken },
          withCredentials: true,
        }
      );
      setBrand((b) => ({ ...b, contact: { ...contactDraft } }));
      setEditingContact(false);
    } catch (err) {
      alert(err?.response?.data?.detail || t("misPage.contactSaveFailed"));
    }
  };

  /* ==================== Guard: Auth ==================== */
  if (!authChecked) {
    return (
      <div className="lab-container">
        <div className="loading">{t("misPage.loading")}</div>
      </div>
    );
  }

  if (mustLogin) {
    return (
      <div className="lab-container">
        <div className="mis-wall-card">
          <h2>{t("misPage.loginWallTitle")}</h2>
          <p>{t("misPage.loginWallText")}</p>
          <div className="cta-row">
            <Link className="btn" to="/login">
              {t("misPage.login")}
            </Link>
            <Link className="btn ghost" to="/register">
              {t("misPage.register")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Brand contact fallback
  const contact =
    brand?.contact &&
    (brand.contact.phone ||
      brand.contact.email ||
      brand.contact.website ||
      brand.contact.note)
      ? brand.contact
      : MIS_DEFAULT_CONTACT;

  /* ==================== Render ==================== */
  return (
    <div className="page lab-container mis-page">
      {/* Üst başlık */}
      <header className="lab-header">
        <div className="lab-title-row">
          <FiBookOpen className="lab-icon" />
          <div>
            <h1 className="lab-title">{t("misPage.headerTitle")}</h1>
            <p className="lab-subtitle">{t("misPage.headerSubtitle")}</p>
          </div>
        </div>
      </header>

      {/* Brand iletişim */}
      <section className="mis-card brand-card">
        <div className="brand-card-row">
          <h2>{t("misPage.contactTitle")}</h2>
          {brand?.can_edit_contact && !editingContact && (
            <button
              className="btn small"
              type="button"
              onClick={() => setEditingContact(true)}
            >
              <FiEdit2 /> {t("misPage.edit")}
            </button>
          )}
          {brand?.can_edit_contact && editingContact && (
            <div className="edit-actions">
              <button
                className="btn small"
                type="button"
                onClick={saveContact}
              >
                <FiSave /> {t("misPage.save")}
              </button>
              <button
                className="btn small ghost"
                type="button"
                onClick={() => {
                  setEditingContact(false);
                  setContactDraft({
                    phone: contact.phone || "",
                    email: contact.email || "",
                    website: contact.website || "",
                    note: contact.note || "",
                  });
                }}
              >
                <FiX /> {t("misPage.cancel")}
              </button>
            </div>
          )}
        </div>

        {!editingContact ? (
          <div className="brand-grid">
            <div>
              <span>{t("misPage.phone")}</span>
              <strong>{contact.phone || "-"}</strong>
            </div>
            <div>
              <span>{t("misPage.email")}</span>
              <strong>{contact.email || "-"}</strong>
            </div>
            <div>
              <span>{t("misPage.website")}</span>
              {contact.website ? (
                <a
                  href={contact.website}
                  target="_blank"
                  rel="noreferrer"
                >
                  {contact.website}
                </a>
              ) : (
                <span>-</span>
              )}
            </div>
            <div className="full">
              <span>{t("misPage.addressNote")}</span>
              <p style={{ whiteSpace: "pre-line" }}>
                {contact.note || "-"}
              </p>
            </div>
          </div>
        ) : (
          <div className="brand-grid edit">
            <div>
              <label>{t("misPage.phone")}</label>
              <input
                value={contactDraft.phone}
                onChange={(e) =>
                  setContactDraft((s) => ({
                    ...s,
                    phone: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label>{t("misPage.email")}</label>
              <input
                value={contactDraft.email}
                onChange={(e) =>
                  setContactDraft((s) => ({
                    ...s,
                    email: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <label>{t("misPage.website")}</label>
              <input
                value={contactDraft.website}
                onChange={(e) =>
                  setContactDraft((s) => ({
                    ...s,
                    website: e.target.value,
                  }))
                }
              />
            </div>
            <div className="full">
              <label>{t("misPage.addressNote")}</label>
              <textarea
                rows={2}
                value={contactDraft.note}
                onChange={(e) =>
                  setContactDraft((s) => ({
                    ...s,
                    note: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        )}
      </section>

      {/* Yeni paylaşım */}
      <section className="mis-card compose-card">
        <div className="compose-header">
          <h2>{t("misPage.composeTitle")}</h2>
          <p className="compose-hint">{t("misPage.composeHint")}</p>
        </div>

        <form onSubmit={submitThread} className="compose-form">
          <div className="compose-row">
            <div className="compose-col">
              <label>{t("misPage.model")}</label>
              <select
                value={modelCode}
                onChange={(e) => setModelCode(e.target.value)}
              >
                {models.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="compose-col">
              <label>
                {t("misPage.subjectLabel")}{" "}
                <small>({subject.trim().length}/10)</small>
              </label>
              <input
                value={subject}
                maxLength={10}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("misPage.subjectPlaceholder")}
                required
              />
            </div>
          </div>

          <label>{t("misPage.content")}</label>
          <textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("misPage.contentPlaceholder")}
          />

          <div className="compose-actions">
            <label
              className={`btn ghost ${!canPost ? "disabled" : ""}`}
              style={{ position: "relative" }}
            >
              <FiUpload />
              {t("misPage.addImage")}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                disabled={!canPost}
                onChange={(e) =>
                  setImageFile(e.target.files?.[0] || null)
                }
              />
            </label>
            {imageFile && (
              <span className="file-chip">{imageFile.name}</span>
            )}

            <button
              className="btn submit"
              type="submit"
              disabled={!canPost || posting}
              title={t("misPage.send")}
            >
              <FiSend aria-hidden="true" />{" "}
              {posting ? t("misPage.sending") : t("misPage.send")}
            </button>
          </div>
        </form>
      </section>

      {/* Filtreler */}
      <section className="mis-card filters-card">
        <div className="filters-row">
          <div className="filters-left">
            <select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
            >
              <option value="ALL">{t("misPage.allModels")}</option>
              {models.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.name}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="latest">{t("misPage.sortNewest")}</option>
              <option value="top">{t("misPage.sortTop")}</option>
            </select>
            <label className="check">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
              />
              <span>{t("misPage.onlyMine")}</span>
            </label>
          </div>

          <div className="filters-divider" aria-hidden="true" />

          <div className="filters-right">
            <input
              className="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("misPage.searchPlaceholder")}
            />
          </div>
        </div>
      </section>

      {/* Liste */}
      <section className="mis-card feed-card">
        {loadingList ? (
          <div className="loading">{t("misPage.loading")}</div>
        ) : threads.length === 0 ? (
          <div className="empty">{t("misPage.noThreads")}</div>
        ) : (
          threads.map((th) => (
            <article key={th.id} className="thread">
              <header className="thread-head">
                <div className="thread-meta">
                  <span className="badge">{th.model_name}</span>
                  <span className="subject" title={th.subject}>
                    {th.subject}
                  </span>
                  <span className="by">• {th.author_display}</span>
                </div>
                <div className="head-right">
                  <button
                    className={`icon-btn thread-like ${
                      th.liked ? "liked" : ""
                    }`}
                    onClick={() => toggleThreadLike(th.id)}
                    title={
                      th.liked
                        ? t("misPage.unlikeThread")
                        : t("misPage.likeThread")
                    }
                    aria-pressed={th.liked ? "true" : "false"}
                  >
                    <FiStar aria-hidden="true" />
                    <span>{th.likes_count ?? 0}</span>
                  </button>
                  <time className="time">
                    {th.created_display}
                  </time>
                  {(th.is_owner || me?.role === "supplier") && (
                    <ThreadMenu
                      thread={th}
                      onChanged={fetchThreads}
                    />
                  )}
                </div>
              </header>

              {/* Soru gövdesi (PEMBE) */}
              <div className="thread-body">
                <span className="thread-label">{t("misPage.question")}</span>
                {th.image_url && (
                  <img
                    alt=""
                    className="thread-img"
                    src={th.image_url}
                  />
                )}
                {th.body && <p>{th.body}</p>}
              </div>

              {/* Cevaplar (MAVİ) */}
              <div className="posts">
                {th.posts?.length > 0 && (
                  <div className="posts-label">{t("misPage.answers")}</div>
                )}
                {th.posts?.map((p) => (
                  <div
                    key={p.id}
                    className={`post ${
                      p.is_supplier_reply ? "supplier" : ""
                    }`}
                  >
                    <div className="post-head">
                      <div className="left">
                        {p.is_supplier_reply && (
                          <span
                            className="pin"
                            title={t("misPage.supplierReplyTitle")}
                          >
                            {t("misPage.supplier")}
                          </span>
                        )}
                        <strong>{p.author_display}</strong>
                        <span className="dot">•</span>
                        <time>{p.created_display}</time>
                      </div>
                      <div className="right">
                        <button
                          className={`icon-btn ${
                            p.liked ? "liked" : ""
                          }`}
                          onClick={() => toggleLike(p.id)}
                          title={
                            p.liked
                              ? t("misPage.unlikePost")
                              : t("misPage.likePost")
                          }
                          aria-pressed={
                            p.liked ? "true" : "false"
                          }
                        >
                          <FiHeart aria-hidden="true" />
                          <span>{p.likes_count}</span>
                        </button>
                        {(p.is_owner ||
                          me?.role === "supplier") && (
                          <PostMenu
                            post={p}
                            onChanged={fetchThreads}
                          />
                        )}
                      </div>
                    </div>
                    {p.image_url && (
                      <img
                        alt=""
                        className="post-img"
                        src={p.image_url}
                      />
                    )}
                    {p.body && (
                      <p className="post-body">{p.body}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Cevap yaz */}
              <div className="reply">
                {!canPost ? (
                  <div className="hint small">
                    {t("misPage.replyLoginRequired")}
                  </div>
                ) : (
                  <>
                    <textarea
                      rows={2}
                      placeholder={t("misPage.replyPlaceholder")}
                      value={replyText[th.id] || ""}
                      onChange={(e) =>
                        setReplyText((s) => ({
                          ...s,
                          [th.id]: e.target.value,
                        }))
                      }
                    />
                    <div className="reply-actions">
                      <label
                        className="btn ghost"
                        style={{ position: "relative" }}
                      >
                        <FiUpload aria-hidden="true" />
                        {t("misPage.image")}
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) =>
                            setReplyImage((s) => ({
                              ...s,
                              [th.id]:
                                e.target.files?.[0] || null,
                            }))
                          }
                        />
                      </label>
                      {replyImage[th.id] && (
                        <span className="file-chip">
                          {replyImage[th.id].name}
                        </span>
                      )}
                      <button
                        className="btn"
                        disabled={!!replyPosting[th.id]}
                        onClick={() => submitReply(th.id)}
                      >
                        <FiMessageCircle aria-hidden="true" />{" "}
                        {replyPosting[th.id]
                          ? t("misPage.replySending")
                          : t("misPage.reply")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </article>
          ))
        )}
      </section>
    </div>
  );
}

/* ====== KEBAB MENÜ BİLEŞENLERİ ====== */

function ThreadMenu({ thread, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [subj, setSubj] = React.useState(thread.subject);
  const [body, setBody] = React.useState(thread.body || "");

  const toggle = () => setOpen((o) => !o);

  const doDelete = async () => {
    if (!window.confirm(t("misPage.confirmDeleteThread")))
      return;
    const { data: csrf } = await axios.get("/api/auth/csrf/");
    await axios.delete(`/api/social/threads/${thread.id}/`, {
      headers: { "X-CSRFToken": csrf?.csrfToken },
      withCredentials: true,
    });
    onChanged?.();
  };

  const doPatch = async () => {
    const { data: csrf } = await axios.get("/api/auth/csrf/");
    await axios.patch(
      `/api/social/threads/${thread.id}/`,
      { subject: subj, body },
      {
        headers: { "X-CSRFToken": csrf?.csrfToken },
        withCredentials: true,
      }
    );
    setEditing(false);
    onChanged?.();
  };

  return (
    <div className="kebab">
      <button
        className="icon-btn ghost"
        onClick={toggle}
        title={t("misPage.options")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
      >
        ⋯
      </button>
      {open && !editing && (
        <div className="menu" role="menu">
          <button
            onClick={() => setEditing(true)}
            role="menuitem"
          >
            {t("misPage.edit")}
          </button>
          <button
            onClick={doDelete}
            className="danger"
            role="menuitem"
          >
            {t("misPage.delete")}
          </button>
        </div>
      )}
      {editing && (
        <div className="edit-pop">
          <label>{t("misPage.subjectMax10")}</label>
          <input
            value={subj}
            maxLength={10}
            onChange={(e) => setSubj(e.target.value)}
          />
          <label>{t("misPage.content")}</label>
          <textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="row">
            <button
              className="btn small"
              type="button"
              onClick={doPatch}
            >
              <FiSave /> {t("misPage.save")}
            </button>
            <button
              className="btn small ghost"
              type="button"
              onClick={() => setEditing(false)}
            >
              <FiX /> {t("misPage.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PostMenu({ post, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [body, setBody] = React.useState(post.body || "");

  const toggle = () => setOpen((o) => !o);

  const doDelete = async () => {
    if (!window.confirm(t("misPage.confirmDeletePost"))) return;
    const { data: csrf } = await axios.get("/api/auth/csrf/");
    await axios.delete(`/api/social/posts/${post.id}/`, {
      headers: { "X-CSRFToken": csrf?.csrfToken },
      withCredentials: true,
    });
    onChanged?.();
  };

  const doPatch = async () => {
    const { data: csrf } = await axios.get("/api/auth/csrf/");
    await axios.patch(
      `/api/social/posts/${post.id}/`,
      { body },
      {
        headers: { "X-CSRFToken": csrf?.csrfToken },
        withCredentials: true,
      }
    );
    setEditing(false);
    onChanged?.();
  };

  return (
    <div className="kebab">
      <button
        className="icon-btn ghost"
        onClick={toggle}
        title={t("misPage.options")}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : "false"}
      >
        ⋯
      </button>
      {open && !editing && (
        <div className="menu" role="menu">
          <button
            onClick={() => setEditing(true)}
            role="menuitem"
          >
            {t("misPage.edit")}
          </button>
          <button
            onClick={doDelete}
            className="danger"
            role="menuitem"
          >
            {t("misPage.delete")}
          </button>
        </div>
      )}
      {editing && (
        <div className="edit-pop">
          <label>{t("misPage.replyLabel")}</label>
          <textarea
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="row">
            <button
              className="btn small"
              type="button"
              onClick={doPatch}
            >
              <FiSave /> {t("misPage.save")}
            </button>
            <button
              className="btn small ghost"
              type="button"
              onClick={() => setEditing(false)}
            >
              <FiX /> {t("misPage.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}